/**
 * CR-4707: Admin Setup → Regional → Decimal Precision controls.
 *
 * Surfaces the company-level `CompanyPreferences.unit_price_decimals` field
 * so admins can pick 0–4 decimals for monetary fields rendered in the PO
 * Line pop-up (Unit Price + Supplier Proposed Unit Price).
 *
 * Customer review 2026-07: also surfaces `kpi_percent_decimals` (0–2) which
 * controls how many decimal places KPI percentage rings display with — so the
 * customer can see e.g. 96.2% instead of a rounded 96%.
 *
 * CR-6155: originally only the Scheduled Dashboard read this, so a company
 * that set 2 decimals still saw a whole number on the main dashboard. It now
 * drives every percentage KPI ring: Default (OTIF, OTD), Group KPI and Group
 * Detail (month/FYTD OTIF and OTD), Site KPI (defect rate and the OTIF
 * month/3-month/12-month comparison) alongside the Scheduled Dashboard.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Calculator, Loader2, Save } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { preferencesAPI } from '../../../lib/api';

const PRICE_DECIMAL_CHOICES = [0, 1, 2, 3, 4] as const;
const KPI_DECIMAL_CHOICES = [0, 1, 2] as const;

export function UnitPriceDecimalsCard() {
  const [value, setValue] = useState<number>(2);
  const [initial, setInitial] = useState<number>(2);
  const [kpiValue, setKpiValue] = useState<number>(0);
  const [kpiInitial, setKpiInitial] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const fetchPrefs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await preferencesAPI.getCompanyPreferences();
      const v = typeof res.data?.unit_price_decimals === 'number' ? res.data.unit_price_decimals : 2;
      const k = typeof res.data?.kpi_percent_decimals === 'number' ? res.data.kpi_percent_decimals : 0;
      setValue(v);
      setInitial(v);
      setKpiValue(k);
      setKpiInitial(k);
    } catch (err: unknown) {
      console.error('Failed to load company preferences:', err);
      toast.error('Could not load decimal precision settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrefs();
  }, [fetchPrefs]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await preferencesAPI.updateCompanyPreferences({
        unit_price_decimals: value,
        kpi_percent_decimals: kpiValue,
      });
      setInitial(value);
      setKpiInitial(kpiValue);
      toast.success('Decimal precision settings saved.');
    } catch (err: unknown) {
      console.error('Failed to update company preferences:', err);
      toast.error('Could not save decimal precision. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const dirty = value !== initial || kpiValue !== kpiInitial;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="h-5 w-5" />
          Decimal Precision
        </CardTitle>
        <CardDescription>
          Unit price decimals control the PO Line pop-up (Unit Price and Supplier
          Proposed Unit Price). KPI percentage decimals control how OTIF, OTD and
          defect-rate percentages display on every dashboard ring. Both apply
          across all users in this company.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-2 w-48">
                <Label htmlFor="unit-price-decimals">Unit price decimal places</Label>
                <select
                  id="unit-price-decimals"
                  value={value}
                  onChange={(e) => setValue(Number(e.target.value))}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background"
                >
                  {PRICE_DECIMAL_CHOICES.map((n) => (
                    <option key={n} value={n}>
                      {n} {n === 1 ? 'decimal' : 'decimals'}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground">Preview</div>
                <div className="px-3 py-2 border border-border rounded-md bg-muted/30 font-mono text-sm">
                  £{value === 0 ? '85' : `84.${('5678').slice(0, value)}`}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-2 w-48">
                <Label htmlFor="kpi-percent-decimals">OTIF / KPI percentage decimal places</Label>
                <p className="text-xs text-muted-foreground">
                  Applies to every dashboard KPI ring.
                </p>
                <select
                  id="kpi-percent-decimals"
                  value={kpiValue}
                  onChange={(e) => setKpiValue(Number(e.target.value))}
                  disabled={saving}
                  className="w-full px-3 py-2 border border-border rounded-md bg-background"
                >
                  {KPI_DECIMAL_CHOICES.map((n) => (
                    <option key={n} value={n}>
                      {n} {n === 1 ? 'decimal' : 'decimals'}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <div className="text-xs text-muted-foreground">Preview</div>
                <div className="px-3 py-2 border border-border rounded-md bg-muted/30 font-mono text-sm">
                  {kpiValue === 0 ? '96' : `96.${('24').slice(0, kpiValue)}`}%
                </div>
              </div>
            </div>

            <Button onClick={handleSave} disabled={!dirty || saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default UnitPriceDecimalsCard;
